import React, { useState, useEffect } from 'react';
import {
  Users, FileText, Calendar, Activity, AlertCircle,
  ChefHat, ClipboardList, Smile, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { getEmployeePhotoUrl } from '../../utils/imageUtils';
import { getDashboardStats } from '../../api/dashboard';
import { employeesAPI } from '../../api/employees';
import { getMenuToday } from '../../api/cafeteria';
import { solicitudesAPI } from '../../api/solicitudes';
import { calendarAPI } from '../../api/calendar';

// ── Donut SVG minimalista ────────────────────────────────────────
function DonutChart({ pct, color = '#2563EB', size = 80, stroke = 10 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const filled = circ * Math.min(Math.max(pct, 0), 1);
  const cx = size / 2;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
      <circle
        cx={cx} cy={cx} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={`${filled} ${circ}`}
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatBirthDate(dateString) {
  if (!dateString) return '';
  const s = String(dateString).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'short' });
}

// ── Mini calendario laboral ─────────────────────────────────────
const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_NAMES   = ['D','L','M','X','J','V','S'];

const TYPE_COLOR = {
  FERIADO_NACIONAL:    'bg-red-100 text-red-700',
  FERIADO_REGIONAL:    'bg-orange-100 text-orange-700',
  FERIADO_EMPRESA:     'bg-blue-100 text-blue-700',
  NO_LABORABLE:        'bg-violet-100 text-violet-700',
  FERIADO_TRABAJABLE:  'bg-amber-100 text-amber-700',
  MEDIO_DIA:           'bg-purple-100 text-purple-700',
};

function MiniCalendarWidget() {
  const [cal,    setCal]    = useState({ events: [], config: null });
  const [navDate, setNavDate] = useState(new Date());

  const year  = navDate.getFullYear();
  const month = navDate.getMonth();

  useEffect(() => {
    Promise.all([
      calendarAPI.getEvents({ anio: year, mes: month + 1 }).catch(() => ({ events: [] })),
      calendarAPI.getConfig().catch(() => null),
    ]).then(([evRes, cfgRes]) => {
      setCal({
        events: evRes?.events || [],
        config: cfgRes?.config || cfgRes?.data || null,
      });
    });
  }, [year, month]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const todayStr = new Date().toLocaleDateString('sv-SE');

  const eventMap = {};
  for (const ev of cal.events) {
    const d = (ev.fecha || '').split('T')[0];
    if (d) eventMap[d] = ev;
  }

  const isWorkDay = (day) => {
    const cfg = cal.config;
    if (!cfg || !day) return true;
    const dow = new Date(year, month, day).getDay();
    const keys = ['trabaja_domingo','trabaja_lunes','trabaja_martes','trabaja_miercoles','trabaja_jueves','trabaja_viernes','trabaja_sabado'];
    return !!cfg[keys[dow]];
  };

  const upcoming = cal.events
    .filter(ev => (ev.fecha || '').split('T')[0] >= todayStr)
    .slice(0, 4);

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-50">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Calendario Laboral</p>
        <div className="flex items-center gap-1">
          <button onClick={() => setNavDate(new Date(year, month - 1, 1))}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft size={14} />
          </button>
          <span className="text-xs font-semibold text-gray-700 w-28 text-center">
            {MONTH_NAMES[month]} {year}
          </span>
          <button onClick={() => setNavDate(new Date(year, month + 1, 1))}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-0.5">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const ev      = eventMap[dateStr];
            const isToday = dateStr === todayStr;
            const isWork  = isWorkDay(day);
            const dow     = new Date(year, month, day).getDay();
            const isWeekend = dow === 0 || dow === 6;

            let cellCls = 'rounded-md text-center text-xs py-1 font-medium transition-colors ';
            if (isToday)           cellCls += 'bg-blue-600 text-white';
            else if (ev && !ev.es_laborable) cellCls += 'bg-red-50 text-red-600 ring-1 ring-red-200';
            else if (ev)           cellCls += 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
            else if (!isWork || isWeekend) cellCls += 'text-gray-300';
            else                   cellCls += 'text-gray-700 hover:bg-gray-50';

            return (
              <div key={i} className={cellCls} title={ev?.nombre}>
                {day}
                {ev && !isToday && <div className="w-1 h-1 rounded-full bg-current mx-auto mt-0.5 opacity-60" />}
              </div>
            );
          })}
        </div>

        {/* Leyenda compacta */}
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          {[
            { cls: 'bg-blue-600', label: 'Hoy' },
            { cls: 'bg-red-200', label: 'Feriado' },
            { cls: 'bg-amber-200', label: 'Especial' },
          ].map(({ cls, label }) => (
            <span key={label} className="flex items-center gap-1 text-[10px] text-gray-400">
              <span className={`w-2 h-2 rounded-sm ${cls} inline-block`} />{label}
            </span>
          ))}
        </div>

        {/* Próximos eventos */}
        {upcoming.length > 0 && (
          <div className="border-t border-gray-50 pt-3 space-y-1.5">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Próximos</p>
            {upcoming.map(ev => {
              const colorCls = TYPE_COLOR[ev.tipo] || 'bg-gray-100 text-gray-600';
              const d = new Date((ev.fecha || '').split('T')[0] + 'T12:00:00');
              const label = d.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
              return (
                <div key={ev.id} className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-gray-400 w-12 shrink-0">{label}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full truncate max-w-full ${colorCls}`}>
                    {ev.nombre}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Vista empleado ───────────────────────────────────────────────
function EmployeeDashboard({ currentUser }) {
  const [selfData,       setSelfData]       = useState(null);
  const [menuHoy,        setMenuHoy]        = useState(null);
  const [solicitudes,    setSolicitudes]    = useState([]);
  const [cumpleanos,     setCumpleanos]     = useState([]);
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    Promise.all([
      employeesAPI.getAll().catch(() => ({ employees: [] })),
      getMenuToday().catch(() => null),
      solicitudesAPI.getAll({ limit: 5 }).catch(() => ({ solicitudes: [] })),
      getDashboardStats().catch(() => null),
    ]).then(([empRes, menuRes, solRes, dashRes]) => {
      const emps = empRes?.employees || [];
      setSelfData(emps[0] || null);
      setMenuHoy(menuRes?.menu || null);
      setSolicitudes(solRes?.solicitudes || []);
      setCumpleanos(dashRes?.data?.cumpleanos_mes || []);
    }).finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString('es-DO', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const hour  = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const ESTADO_CFG = {
    pendiente:   { cls: 'bg-amber-100 text-amber-700'  },
    en_revision: { cls: 'bg-blue-100 text-blue-700'    },
    aprobada:    { cls: 'bg-green-100 text-green-700'  },
    completada:  { cls: 'bg-teal-100 text-teal-700'    },
    rechazada:   { cls: 'bg-red-100 text-red-600'      },
    cancelada:   { cls: 'bg-gray-100 text-gray-500'    },
    en_proceso:  { cls: 'bg-purple-100 text-purple-700'},
  };

  return (
    <div className="space-y-5">

      {/* ── Bienvenida ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selfData?.foto ? (
            <img src={getEmployeePhotoUrl(selfData.foto)} alt={selfData.nombre}
              className="w-12 h-12 rounded-full object-cover border border-gray-200 shrink-0"
              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
          ) : null}
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-lg font-bold text-blue-600 shrink-0"
            style={{ display: selfData?.foto ? 'none' : 'flex' }}>
            {(selfData?.nombre || currentUser?.username || '?')[0].toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {greeting}, {selfData?.nombre || currentUser?.username} 👋
            </h1>
            {selfData && (
              <p className="text-sm text-gray-400 mt-0.5">
                {selfData.cargo || selfData.cargo_nombre || ''}
                {(selfData.cargo || selfData.cargo_nombre) && (selfData.departamento || selfData.departamento_nombre) ? ' · ' : ''}
                {selfData.departamento || selfData.departamento_nombre || ''}
              </p>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-400 capitalize hidden sm:block">{today}</p>
      </div>

      {/* ── Fila rápida ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        {/* Menú hoy */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center shrink-0">
            <ChefHat size={18} className="text-orange-500" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-400 font-medium">Menú de hoy</p>
            {menuHoy ? (
              <p className="text-sm font-semibold text-gray-800 truncate">
                {(menuHoy.opciones || []).length} opción{(menuHoy.opciones || []).length !== 1 ? 'es' : ''} disponible{(menuHoy.opciones || []).length !== 1 ? 's' : ''}
              </p>
            ) : (
              <p className="text-sm text-gray-400">Sin menú publicado</p>
            )}
          </div>
        </div>

        {/* Mis solicitudes activas */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center shrink-0">
            <ClipboardList size={18} className="text-purple-500" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Solicitudes</p>
            <p className="text-sm font-semibold text-gray-800">
              {solicitudes.filter(s => !['completada','cancelada','rechazada'].includes(s.estado)).length} activas
            </p>
          </div>
        </div>

        {/* Cumpleaños del mes */}
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-pink-50 rounded-xl flex items-center justify-center shrink-0">
            <Smile size={18} className="text-pink-500" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium">Cumpleaños este mes</p>
            <p className="text-sm font-semibold text-gray-800">{cumpleanos.length} compañeros</p>
          </div>
        </div>
      </div>

      {/* ── Dos columnas ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Mis solicitudes recientes */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mis solicitudes recientes</p>
          </div>
          {solicitudes.length === 0 ? (
            <div className="py-10 text-center">
              <ClipboardList size={28} className="text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Sin solicitudes registradas</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {solicitudes.slice(0, 5).map(s => {
                const cfg = ESTADO_CFG[s.estado] || ESTADO_CFG.pendiente;
                return (
                  <div key={s.id} className="flex items-center gap-3 px-5 py-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{s.asunto}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{s.categoria_label || s.categoria}</p>
                    </div>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${cfg.cls}`}>
                      {s.estado_label || s.estado}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cumpleaños del mes */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Cumpleaños del mes</p>
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
              {new Date().toLocaleDateString('es-DO', { month: 'long' })}
            </span>
          </div>
          {cumpleanos.length === 0 ? (
            <div className="py-10 text-center">
              <Calendar size={28} className="text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No hay cumpleaños este mes</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
              {cumpleanos.map(emp => (
                <div key={emp.id} className="flex items-center gap-3 px-5 py-3">
                  {emp.foto ? (
                    <img src={getEmployeePhotoUrl(emp.foto)} alt={emp.nombre}
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  ) : null}
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0"
                    style={{ display: emp.foto ? 'none' : 'flex' }}>
                    <span className="text-xs font-bold text-blue-600">{emp.nombre?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {emp.nombre} {emp.apellido}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{emp.departamento || '—'}</p>
                  </div>
                  <span className="text-xs font-semibold text-blue-600 shrink-0">
                    {formatBirthDate(emp.fecha_nacimiento)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Calendario laboral ────────────────────────────────────── */}
      <MiniCalendarWidget />
    </div>
  );
}

// ── Vista admin ──────────────────────────────────────────────────
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getDashboardStats();
      setStats(data.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('es-DO', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={load} className="mt-3 px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
          Reintentar
        </button>
      </div>
    );
  }

  const st               = stats?.estadisticas || {};
  const totalEmpleados   = st.total_empleados       || 0;
  const activos          = st.empleados_activos     || 0;
  const inactivos        = st.empleados_inactivos   || 0;
  const contratosActivos = st.contratos_activos     || 0;
  const contratosTermin  = st.contratos_terminados  || 0;
  const accionesPend     = st.acciones_pendientes   || 0;
  const docsBorrador     = st.documentos_pendientes || 0;
  const cumpleanos       = stats?.cumpleanos_mes    || [];
  const pctActivos       = totalEmpleados > 0 ? activos / totalEmpleados : 0;

  const kpis = [
    { label: 'Empleados Activos', value: activos,          sub: `${inactivos} inactivos`,       color: 'text-blue-600',  icon: <Users size={16} className="text-blue-400" /> },
    { label: 'Contratos Activos', value: contratosActivos, sub: `${contratosTermin} terminados`, color: 'text-green-600', icon: <FileText size={16} className="text-green-400" /> },
    { label: 'Cumpleaños del Mes',value: cumpleanos.length,sub: new Date().toLocaleDateString('es-DO', { month: 'long' }), color: 'text-orange-500', icon: <Calendar size={16} className="text-orange-400" /> },
    { label: 'Acciones Pendientes',value: accionesPend,   sub: 'requieren aprobación',          color: accionesPend > 0 ? 'text-amber-500' : 'text-gray-400', icon: <Activity size={16} className={accionesPend > 0 ? 'text-amber-400' : 'text-gray-300'} /> },
    { label: 'Docs en Borrador',   value: docsBorrador,   sub: 'por revisar',                   color: docsBorrador > 0 ? 'text-red-500'   : 'text-gray-400', icon: <AlertCircle size={16} className={docsBorrador > 0 ? 'text-red-400' : 'text-gray-300'} /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400 capitalize">{today}</p>
        <button onClick={load} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide leading-tight">{k.label}</span>
              {k.icon}
            </div>
            <p className={`text-3xl font-bold ${k.color}`}>{k.value}</p>
            <p className="text-xs text-gray-400 mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-5">Resumen General</h2>
          <div className="flex items-center gap-5 mb-6">
            <div className="relative flex-shrink-0">
              <DonutChart pct={pctActivos} color="#2563EB" size={88} stroke={10} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-gray-900">{Math.round(pctActivos * 100)}%</span>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalEmpleados}</p>
              <p className="text-sm text-gray-500">empleados en total</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-600 inline-block" />
                <span className="text-xs text-gray-400">{activos} activos</span>
                <span className="w-2 h-2 rounded-full bg-gray-200 inline-block ml-2" />
                <span className="text-xs text-gray-400">{inactivos} inactivos</span>
              </div>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              { label: 'Total Empleados',     value: totalEmpleados,   color: 'text-gray-900'  },
              { label: 'Empleados Activos',   value: activos,          color: 'text-blue-600'  },
              { label: 'Empleados Inactivos', value: inactivos,        color: 'text-gray-500'  },
              { label: 'Contratos Activos',   value: contratosActivos, color: 'text-green-600' },
              { label: 'Contratos Terminados',value: contratosTermin,  color: 'text-gray-500'  },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-600">{row.label}</span>
                <span className={`text-sm font-bold ${row.color}`}>{row.value}</span>
              </div>
            ))}
            {accionesPend > 0 && (
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-600">Acciones Pendientes</span>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-600"><Activity size={12} />{accionesPend}</span>
              </div>
            )}
            {docsBorrador > 0 && (
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-600">Docs en Borrador</span>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-red-500"><AlertCircle size={12} />{docsBorrador}</span>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Cumpleaños del Mes</h2>
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">{cumpleanos.length} este mes</span>
          </div>
          {cumpleanos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar size={36} className="text-gray-200 mb-3" />
              <p className="text-sm text-gray-400">No hay cumpleaños este mes</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-72">
              <div className="grid grid-cols-12 pb-2 border-b border-gray-100 mb-1">
                <span className="col-span-1" />
                <span className="col-span-6 text-xs text-gray-400 font-medium uppercase tracking-wide">Empleado</span>
                <span className="col-span-3 text-xs text-gray-400 font-medium uppercase tracking-wide">Departamento</span>
                <span className="col-span-2 text-xs text-gray-400 font-medium uppercase tracking-wide text-right">Fecha</span>
              </div>
              {cumpleanos.map((emp) => (
                <div key={emp.id} className="grid grid-cols-12 items-center py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors rounded">
                  <div className="col-span-1">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">{emp.nombre?.[0]?.toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <p className="text-sm font-semibold text-gray-900 truncate">{emp.nombre} {emp.apellido}</p>
                    <p className="text-xs text-gray-400">{emp.employee_id}</p>
                  </div>
                  <div className="col-span-3">
                    <span className="text-xs text-gray-500 truncate block">{emp.departamento || '—'}</span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-xs font-semibold text-blue-600">{formatBirthDate(emp.fecha_nacimiento)}</span>
                    <p className="text-xs text-gray-400">{emp.edad} años</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Calendario laboral ──────────────────────────────────── */}
      <MiniCalendarWidget />
    </div>
  );
}

// ── Export ───────────────────────────────────────────────────────
export default function Dashboard({ currentUser }) {
  const isEmployee = currentUser?.role === 'employee';
  return isEmployee
    ? <EmployeeDashboard currentUser={currentUser} />
    : <AdminDashboard />;
}
